/**
 * VimsamshaDivisionsInfoTable Component
 * Displays D20 Vimsamsha Guruvadi system - 20 Ayurvedic qualities
 * Each sign divided into 20 parts of 1°30' each
 */

import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack
} from '@chakra-ui/react';
import { JatakaResponse } from '../../../../../models/api';
import {
  calculateVimsamshaDivisions,
  VimsamshaAttributes,
  VIMSAMSHA_GUNAS,
  VimsamshaDivision
} from '../data/VimsamshaDivisionsTableData';
import { BLACK_ONLY_ASHTAMSHA_COLORS, getAshtamshaPlanetColor } from '../../../../../config/ashtamshaColors';
import { createPlanetDisplay } from '../helpers/planetDisplayHelper';
import {
  TABLE_WRAPPER_INLINE_STYLES,
  TABLE_CONTAINER_STYLES,
  TABLE_BASE_STYLES,
  TABLE_HEADER_ALT_STYLES,
  TABLE_ROW_HOVER_STYLES,
  TABLE_TD_BASE_STYLES,
  TABLE_TD_SMALL_STYLES,
  TABLE_TD_CENTER_STYLES,
  PLANET_TEXT_STYLES,
  EMPTY_MESSAGE_BOX_STYLES,
  EMPTY_CELL_TEXT_STYLES,
  COLUMN_STYLES
} from './styles/tableStyles';

export interface VimsamshaDivisionsInfoTableProps {
  apiResponse?: JatakaResponse | null;
}

/**
 * Row structure for display
 */
interface VimsamshaRow {
  partNumber: number;
  division: VimsamshaDivision;
  planets: VimsamshaAttributes[];
}

/**
 * Create all 20 rows with planets grouped by part
 */
function groupByPartNumber(divisions: VimsamshaAttributes[]): VimsamshaRow[] {
  const rows: VimsamshaRow[] = [];
  const planetsByPart: Record<number, VimsamshaAttributes[]> = {};

  // Initialize all 20 parts
  for (let i = 1; i <= 20; i++) {
    planetsByPart[i] = [];
  }

  // Group planets by part number
  divisions.forEach(div => {
    planetsByPart[div.partNumber].push(div);
  });

  // Create rows with all info
  for (let i = 1; i <= 20; i++) {
    const planetsInPart = planetsByPart[i];
    const division = VIMSAMSHA_GUNAS[i - 1];

    rows.push({
      partNumber: i,
      division,
      planets: planetsInPart
    });
  }

  return rows;
}

/**
 * Format dosha display (primary + secondary if exists)
 */
const formatDoshaDisplay = (primaryDosha: string, secondaryDosha?: string): string => {
  if (secondaryDosha) {
    return `${primaryDosha} (${secondaryDosha})`;
  }
  return primaryDosha;
};

export const VimsamshaDivisionsInfoTable: React.FC<VimsamshaDivisionsInfoTableProps> = ({ apiResponse }) => {
  if (!apiResponse) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>No chart data available</Text>
      </Box>
    );
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  // Calculate Vimsamsha divisions for all planets
  const { divisions } = calculateVimsamshaDivisions(apiResponse);

  // Group by part number
  const rows = groupByPartNumber(divisions);

  return (
    <Box {...TABLE_WRAPPER_INLINE_STYLES}>
      {/* Vimsamsha Guruvadi Table */}
      <TableContainer {...TABLE_CONTAINER_STYLES}>
        <Table {...TABLE_BASE_STYLES}>
          <Thead {...TABLE_HEADER_ALT_STYLES}>
            <Tr>
              <Th {...COLUMN_STYLES.large}>Planets</Th>
              <Th {...COLUMN_STYLES.xsmall}>Part</Th>
              <Th {...COLUMN_STYLES.medium}>GUNA</Th>
              <Th {...COLUMN_STYLES.medium}>DOSHA</Th>
              <Th {...COLUMN_STYLES.xxlarge}>Meaning</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => {
              return (
                <Tr key={row.partNumber} _hover={TABLE_ROW_HOVER_STYLES}>
                  {/* Planets column */}
                  <Td {...TABLE_TD_BASE_STYLES} minW="80px">
                    {row.planets.length > 0 ? (
                      <HStack spacing={1} flexWrap="wrap">
                        {row.planets.map((planetAttr, idx) => {
                          const planet = planetAttr.planet;
                          const planetDisplay = createPlanetDisplay(planet, currentHoraPlanet);
                          const colors = getAshtamshaPlanetColor(planetDisplay.planetType, BLACK_ONLY_ASHTAMSHA_COLORS);

                          return (
                            <Text
                              key={`${planet.short_name}-${idx}`}
                              {...PLANET_TEXT_STYLES}
                              color={colors.color}
                              bg={colors.backgroundColor}
                              border={colors.borderColor ? `1px solid` : 'none'}
                              borderColor={colors.borderColor}
                            >
                              {planetDisplay.displayText}
                            </Text>
                          );
                        })}
                      </HStack>
                    ) : (
                      <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                    )}
                  </Td>

                  {/* Part Number */}
                  <Td {...TABLE_TD_CENTER_STYLES} fontWeight="normal">
                    <Text fontSize="xs">
                      {row.partNumber}
                    </Text>
                  </Td>

                  {/* GUNA - transliteration */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text fontSize="xs" fontWeight="medium">
                      {row.division.guna}
                    </Text>
                  </Td>

                  {/* DOSHA (primary + secondary) */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text fontSize="xs" fontWeight="normal">
                      {formatDoshaDisplay(row.division.primaryDosha, row.division.secondaryDosha)}
                    </Text>
                  </Td>

                  {/* Meaning (English translation) */}
                  <Td {...TABLE_TD_SMALL_STYLES} fontWeight="normal">
                    <Text fontSize="xs" color="gray.700">
                      {row.division.meaning}
                    </Text>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
